/**
 * Mirrors Prisma's `ImageStatus`. Declared standalone so the browser bundle
 * never imports `@prisma/client`.
 */
export const ImageStatus = {
  Idle: 'IDLE',
  Queued: 'QUEUED',
  Downloading: 'DOWNLOADING',
  Processing: 'PROCESSING',
  Completed: 'COMPLETED',
  Failed: 'FAILED'
} as const

export type ImageStatus = (typeof ImageStatus)[keyof typeof ImageStatus]

/** One WordPress attachment, as the API returns it. */
export interface ImageDTO {
  id: string
  wpMediaId: number
  title: string
  sourceUrl: string
  mimeType: string
  width: number
  height: number
  /** What WordPress held at fetch time. Never overwritten locally. */
  originalAlt: string
  generatedAlt: string | null
  editedAlt: string | null
  status: ImageStatus
  approved: boolean
  saved: boolean
  savedAt: string | null
  error: string | null
  updatedAt: string
}

/** True when WordPress already had alt text for this attachment. */
export function hasOriginalAlt(image: Pick<ImageDTO, 'originalAlt'>): boolean {
  return image.originalAlt.trim().length > 0
}

/** The alt text that would be written to WordPress right now. */
export function effectiveAlt(
  image: Pick<ImageDTO, 'editedAlt' | 'generatedAlt' | 'originalAlt'>
): string {
  return image.editedAlt ?? image.generatedAlt ?? image.originalAlt
}

/** True when there is anything worth approving. */
export function isApprovable(
  image: Pick<ImageDTO, 'editedAlt' | 'generatedAlt' | 'originalAlt'>
): boolean {
  return effectiveAlt(image).trim().length > 0
}

/* ── Decorative images ────────────────────────────────────────────────────────
   Icons, logos and spacers do not benefit from generated alt text — a screen
   reader is better served by an empty alt on a decorative element than by a
   description of a chevron. They are excluded from bulk generation so they
   cannot flood the review queue, and given their own filter so the exclusion is
   visible rather than mysterious.

   Generating for one of them explicitly (the per-card Regenerate button) is
   still allowed; only bulk selection skips them.
   ─────────────────────────────────────────────────────────────────────────── */

export const DECORATIVE_MAX_WIDTH = 100

/** PNG is the de facto format for icons and logos in a WordPress media library. */
export const DECORATIVE_MIME_TYPES = ['image/png'] as const

export function isDecorative(image: Pick<ImageDTO, 'mimeType' | 'width'>): boolean {
  if (DECORATIVE_MIME_TYPES.includes(image.mimeType as (typeof DECORATIVE_MIME_TYPES)[number])) {
    return true
  }
  // width 0 means "not known yet" (fetched before dimensions were recorded), so
  // it must not count as narrow or every such row would be skipped.
  return image.width > 0 && image.width < DECORATIVE_MAX_WIDTH
}

export const MEDIA_FILTERS = [
  'all',
  'missing',
  'original-alt',
  'needs-approval',
  'needs-save',
  'saved',
  'failed',
  'decorative'
] as const

export type MediaFilter = (typeof MEDIA_FILTERS)[number]

export const MEDIA_FILTER_LABELS: Record<MediaFilter, string> = {
  all: 'All',
  missing: 'Missing alt',
  'original-alt': 'Has alt (original)',
  'needs-approval': 'Needs approval',
  'needs-save': 'Not saved',
  saved: 'Saved',
  failed: 'Failed',
  decorative: 'Decorative'
}

/** Per-filter row counts — drives the number badge on each filter pill. */
export type MediaCounts = Record<MediaFilter, number>

export interface MediaPageDTO {
  items: ImageDTO[]
  page: number
  perPage: number
  total: number
  totalPages: number
  counts: MediaCounts
}

export const MEDIA_SORTS = ['newest', 'oldest', 'title', 'updated'] as const
export type MediaSort = (typeof MEDIA_SORTS)[number]
